import {
  FormicWebPlugin,
  FormicWebPluginRegistrationApi,
  WEB_PLUGIN_CONTRACT_VERSION,
  WebPluginDescriptor,
} from './contracts';

declare global {
  interface Window {
    FormicWebPlugins?: FormicWebPluginRegistrationApi;
  }
}

const registrations = new Map<string, FormicWebPlugin>();
const loadedEntryHashes = new Map<string, string>();
const activeMountCounts = new Map<string, number>();
const pendingLoads = new Map<string, Promise<FormicWebPlugin>>();
const identityPattern = /^[A-Za-z][A-Za-z0-9.-]{2,127}$/;
const versionPattern = /^[0-9]+\.[0-9]+\.[0-9]+(?:-[0-9A-Za-z.-]+)?$/;

const key = (id: string, version: string): string => `${id}\n${version}`;

export interface WebPluginRuntimeState {
  registered: boolean;
  loaded: boolean;
  active: boolean;
  declaredEvents: string[];
  registeredEvents: string[];
}

export const readWebPluginRuntimeState = (
  descriptor: WebPluginDescriptor,
): WebPluginRuntimeState => {
  const registrationKey = key(descriptor.id, descriptor.version);
  const plugin = registrations.get(registrationKey);
  const loaded = !!plugin && loadedEntryHashes.get(registrationKey) === descriptor.entryHash;
  const active = loaded && (activeMountCounts.get(registrationKey) ?? 0) > 0;
  const events = plugin
    ? [typeof plugin.onPageLoad === 'function' ? 'onPageLoad' : null, 'mount'].filter(
        (event): event is string => event !== null,
      )
    : [];
  return {
    registered: !!plugin,
    loaded,
    active,
    declaredEvents: events,
    registeredEvents: loaded ? events : [],
  };
};

export const activateWebPlugin = (descriptor: WebPluginDescriptor): (() => void) => {
  const registrationKey = key(descriptor.id, descriptor.version);
  if (
    !registrations.has(registrationKey) ||
    loadedEntryHashes.get(registrationKey) !== descriptor.entryHash
  )
    throw new Error('PLUGIN_NOT_LOADED');

  activeMountCounts.set(registrationKey, (activeMountCounts.get(registrationKey) ?? 0) + 1);
  let active = true;
  return () => {
    if (!active) return;
    active = false;
    const remaining = (activeMountCounts.get(registrationKey) ?? 1) - 1;
    if (remaining > 0) activeMountCounts.set(registrationKey, remaining);
    else activeMountCounts.delete(registrationKey);
  };
};

const registrationApi: FormicWebPluginRegistrationApi = Object.freeze({
  contractVersion: WEB_PLUGIN_CONTRACT_VERSION,
  register: <TData>(plugin: FormicWebPlugin<TData>): void => {
    if (
      !plugin ||
      !identityPattern.test(plugin.id) ||
      !versionPattern.test(plugin.version) ||
      typeof plugin.mount !== 'function' ||
      (plugin.onPageLoad !== undefined && typeof plugin.onPageLoad !== 'function')
    )
      throw new Error('PLUGIN_REGISTRATION_INVALID');

    const registrationKey = key(plugin.id, plugin.version);
    if (registrations.has(registrationKey)) throw new Error('PLUGIN_REGISTRATION_DUPLICATE');
    registrations.set(registrationKey, Object.freeze(plugin) as unknown as FormicWebPlugin);
  },
});

export const installWebPluginRegistrationApi = (): FormicWebPluginRegistrationApi => {
  if (window.FormicWebPlugins && window.FormicWebPlugins !== registrationApi)
    throw new Error('PLUGIN_HOST_CONFLICT');
  window.FormicWebPlugins = registrationApi;
  return registrationApi;
};

export const loadWebPlugin = (descriptor: WebPluginDescriptor): Promise<FormicWebPlugin> => {
  installWebPluginRegistrationApi();
  const registrationKey = key(descriptor.id, descriptor.version);
  const existing = registrations.get(registrationKey);
  if (existing) {
    return loadedEntryHashes.get(registrationKey) === descriptor.entryHash
      ? Promise.resolve(existing)
      : Promise.reject(new Error('PLUGIN_RELOAD_REQUIRED'));
  }

  const pending = pendingLoads.get(registrationKey);
  if (pending) return pending;

  const entry = new URL(descriptor.entryUrl, window.location.origin);
  if (
    entry.origin !== window.location.origin ||
    !entry.pathname.startsWith('/bff/plugins/') ||
    !entry.pathname.endsWith('/entry.js')
  )
    return Promise.reject(new Error('PLUGIN_ENTRY_URL_INVALID'));

  const load = new Promise<FormicWebPlugin>((resolve, reject) => {
    const script = document.createElement('script');
    script.src = entry.toString();
    script.async = true;
    script.dataset.formicPlugin = `${descriptor.id}@${descriptor.version}`;
    script.onload = () => {
      const registered = registrations.get(registrationKey);
      if (!registered) {
        reject(new Error('PLUGIN_DID_NOT_REGISTER'));
        return;
      }
      if (registered.id !== descriptor.id || registered.version !== descriptor.version) {
        reject(new Error('PLUGIN_IDENTITY_MISMATCH'));
        return;
      }
      loadedEntryHashes.set(registrationKey, descriptor.entryHash);
      resolve(registered);
    };
    script.onerror = () => reject(new Error('PLUGIN_ENTRY_LOAD_FAILED'));
    document.head.appendChild(script);
  }).finally(() => pendingLoads.delete(registrationKey));

  pendingLoads.set(registrationKey, load);
  return load;
};
